import { useParams } from "react-router-dom";
import "./VoucherDetailNew.css";
import "./ApprovalActions.css";
import { useGetDetail } from "../../hooks/useGetDetail";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import {
  RotateCcw,
  User,
  Stethoscope,
  Receipt,
  DollarSign,
  CreditCard,
  Wallet,
  Phone,
  Calendar,
  Building,
} from "lucide-react";
import { usePost } from "../../hooks/usePost";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useState } from "react";
import formatDate from "../../utils/formatDate";

const VoucherDetailPage = () => {
  const { id } = useParams();

  const { data, loading } = useGetDetail("treatment-voucher", id);
  const [approveModal, setApproveModal] = useState({
    open: false,
    refund: null,
  });
  const { loading: postLoading, postData } = usePost(
    `ho/refund/treatment-voucher/${id}`,
  );
  const voucher = data ? data[0] : null;

  const handleApprove = () => {
    setApproveModal({ open: true, refund: null });
  };

  const confirmApprove = async () => {
    await postData({});
    setApproveModal({ open: false, refund: null });
  };
  const closeApproveModal = () => {
    setApproveModal({ open: false, refund: null });
  };

  return (
    <>
      <div className="px-3 py-2">
        <h1 className="text-lg"> Voucher Detail </h1>
        <Breadcrumb
          currentPage={"Voucher Detail"}
          links={[{ title: "Vouchers", path: "/voucher" }]}
        />
      </div>
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading data...</div>
        </div>
      ) : (
        <div className="voucher-detail-container">
          {/* Header Section */}
          <div className="voucher-header">
            <div className="voucher-header-top">
              <h1 className="voucher-title">Voucher Detail</h1>
              <div className="voucher-status paid">
                {voucher?.Refund ? "Refunded" : "Paid"}
              </div>
            </div>
            <div className="voucher-header-info">
              <div className="voucher-info-item">
                <span className="voucher-info-label">Voucher ID</span>
                <span className="voucher-info-value">
                  {voucher?.code || "N/A"}
                </span>
              </div>
              <div className="voucher-info-item">
                <span className="voucher-info-label">Created</span>
                <span className="voucher-info-value">
                  {voucher?.createdAt
                    ? new Date(voucher.createdAt).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
              <div className="voucher-info-item">
                <span className="voucher-info-label">Last Updated</span>
                <span className="voucher-info-value">
                  {voucher?.updatedAt
                    ? new Date(voucher.updatedAt).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="financial-summary">
            <div className="summary-card">
              <div className="summary-card-header">
                <div className="summary-icon total">
                  <DollarSign size={20} />
                </div>
                <div className="summary-label">Total Amount</div>
              </div>
              <div className="summary-amount">
                {voucher ? voucher?.totalAmount?.toLocaleString() : 0} MMK
              </div>
            </div>

            {voucher?.firstPaidAmount > 0 && voucher?.secondAmount > 0 ? (
              <>
                <div className="summary-card">
                  <div className="summary-card-header">
                    <div className="summary-icon paid">
                      <CreditCard size={20} />
                    </div>
                    <div>
                      <div className="summary-label">
                      First Paid Amount{" "}
                    </div>
                    <p>
                      {voucher?.paymentType === "Bank" &&
                        voucher?.relatedBank?.name &&
                        `(${voucher.relatedBank.name})`}
                    </p>
                    </div>
                  </div>
                  <div className="summary-amount positive">
                    {voucher.firstPaidAmount.toLocaleString()} MMK
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card-header">
                    <div className="summary-icon paid">
                      <CreditCard size={20} />
                    </div>
                    <div>
                      <div className="summary-label">
                      Second Paid Amount{" "}
                    </div>
                    <p>
                      {voucher?.secondAccountType === "bank" &&
                        voucher?.secondAccount?.name &&
                        `(${voucher.secondAccount.name})`}
                    </p>
                    </div>
                  </div>
                  <div className="summary-amount positive">
                    {voucher.secondAmount.toLocaleString()} MMK
                  </div>
                </div>
              </>
            ) : (
              <div className="summary-card">
                <div className="summary-card-header">
                  <div className="summary-icon paid">
                    <CreditCard size={20} />
                  </div>
                  <div>
                    <div className="summary-label">
                    Paid Amount{" "}
                  </div>
                  <p>
                    {voucher?.paymentType === "Bank" &&
                      voucher?.relatedBank?.name &&
                      `(${voucher.relatedBank.name})`}
                  </p>
                  </div>
                </div>
                <div className="summary-amount positive">
                  {voucher ? voucher?.totalPaidAmount?.toLocaleString() : 0} MMK
                </div>
              </div>
            )}

            <div className="summary-card">
              <div className="summary-card-header">
                <div className="summary-icon refunded">
                  <Wallet size={20} />
                </div>
                <div className="summary-label">Refunded</div>
              </div>
              <div className="summary-amount negative">
                {voucher?.refundAmount?.toLocaleString() || 0} MMK
              </div>
            </div>
          </div>

          {/* Information Sections */}
          <div className="info-sections">
            {/* Patient Information */}
            <div className="info-card">
              <div className="info-card-header">
                <div className="info-icon patient">
                  <User size={20} />
                </div>
                <h3 className="info-title">Patient Information</h3>
              </div>
              <div className="info-item">
                <div className="info-label">Full Name</div>
                <div className="info-value">
                  {voucher?.relatedPatient?.name || "N/A"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Patient ID</div>
                <div className="info-value">
                  {voucher?.relatedPatient?.patientID || "N/A"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Phone</div>
                <div className="info-value with-icon">
                  <Phone size={16} className="info-small-icon" />
                  {voucher?.relatedPatient?.phone || "N/A"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Gender / Age</div>
                <div className="info-value">
                  {voucher?.relatedPatient?.gender || "N/A"},{" "}
                  {voucher?.relatedPatient?.age || "N/A"} yrs
                </div>
              </div>
            </div>

            {/* Doctor Information */}
            <div className="info-card">
              <div className="info-card-header">
                <div className="info-icon doctor">
                  <Stethoscope size={20} />
                </div>
                <h3 className="info-title">Doctor Information</h3>
              </div>
              <div className="info-item">
                <div className="info-label">Name</div>
                <div className="info-value">
                  {voucher?.relatedDoctor?.name || "N/A"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Specialization</div>
                <div className="info-value with-icon">
                  <Building size={16} className="info-small-icon" />
                  {voucher?.relatedDoctor?.speciality || "N/A"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Commission</div>
                <div className="info-value">
                  {voucher?.relatedDoctor?.commissionAmount?.toLocaleString() ||
                    0}{" "}
                  MMK
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Schedule</div>
                <div className="info-value with-icon">
                  <Calendar size={16} className="info-small-icon" />
                  {formatDate(voucher?.relatedDoctor?.schedule) || "N/A"}
                </div>
              </div>
            </div>

            {/* Voucher Information */}
            <div className="info-card">
              <div className="info-card-header">
                <div className="info-icon voucher">
                  <Receipt size={20} />
                </div>
                <h3 className="info-title">Voucher Information</h3>
              </div>
              <div className="info-item">
                <div className="info-label">Code</div>
                <div className="info-value" style={{ fontFamily: "monospace" }}>
                  {voucher?.code || "N/A"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Method</div>
                <div className="info-value">
                  {voucher?.paymentMethod || "N/A"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Total</div>
                <div className="info-value">
                  {voucher?.totalAmount?.toLocaleString() || 0} MMK
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Balance</div>
                <div className="info-value">
                  {(
                    voucher?.balance
                  )?.toLocaleString() || 0}{" "}
                  MMK
                </div>
              </div>
            </div>
          </div>

          {/* Treatment Items Table */}
          {voucher?.multiTreatment?.length > 0 && (
            <div className="treatment-section">
              <h3 className="treatment-header">Treatment Items</h3>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: "none", border: "none" }}
              >
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Treatment
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        QTY
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Unit Price
                      </TableCell>
                      {/* <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Discount
                      </TableCell> */}
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {voucher?.multiTreatment?.map((treatment, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": { backgroundColor: "#fafbfc" },
                          borderBottom: "1px solid #f8fafc",
                        }}
                      >
                        <TableCell
                          sx={{
                            color: "#475569",
                            fontWeight: 600,
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          {treatment?.item_id?.name || "N/A"}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: "#64748b",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          {treatment?.qty}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: "#64748b",
                            fontFamily: "Monaco, Menlo, monospace",
                          }}
                        >
                          {treatment?.price?.toLocaleString()} MMK
                        </TableCell>
                        {/* <TableCell
                          align="right"
                          sx={{
                            color: "#dc2626",
                            fontFamily: "Monaco, Menlo, monospace",
                          }}
                        >
                          {(treatment?.totalDiscount || treatment?.discountAmount || 0)?.toLocaleString()} MMK
                        </TableCell> */}
                        <TableCell
                          align="right"
                          sx={{
                            color: "#475569",
                            fontWeight: 600,
                            fontFamily: "Monaco, Menlo, monospace",
                          }}
                        >
                          {(treatment?.qty * treatment?.price).toLocaleString()}{" "}
                          MMK
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <div className="treatment-summary">
                <div className="treatment-summary-row">
                  <span className="treatment-summary-label">Subtotal</span>
                  <span className="treatment-summary-value">
                    {voucher?.totalAmount?.toLocaleString()} MMK
                  </span>
                </div>
                <div className="treatment-summary-row">
                  <span className="treatment-summary-label">Discount</span>
                  <span className="treatment-summary-value discount">
                    {voucher?.totalDiscount?.toLocaleString() || 0} MMK
                  </span>
                </div>
                <div className="treatment-summary-row">
                  <span className="treatment-summary-label total">
                    Total Due
                  </span>
                  <span className="treatment-summary-value total">
                    {(voucher?.totalAmount - (voucher?.totalDiscount || 0))?.toLocaleString()} MMK
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Package Items Table */}
          {voucher?.selectedPackages?.length > 0 && (
            <div className="treatment-section">
              <h3 className="treatment-header">Package Items</h3>
              <TableContainer
                component={Paper}
                sx={{ boxShadow: "none", border: "none" }}
              >
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Treatment Package
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        QTY
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Unit Price
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Discount
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#94a3b8",
                          fontWeight: 600,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "2px solid #f1f5f9",
                        }}
                      >
                        Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {voucher?.selectedPackages?.map(
                      (treatmentPackage, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": { backgroundColor: "#fafbfc" },
                            borderBottom: "1px solid #f8fafc",
                          }}
                        >
                          <TableCell
                            sx={{
                              color: "#475569",
                              fontWeight: 600,
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            {treatmentPackage?.item_id?.name || "N/A"}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              color: "#64748b",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            {treatmentPackage?.qty}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: "#64748b",
                              fontFamily: "Monaco, Menlo, monospace",
                            }}
                          >
                            {treatmentPackage?.price?.toLocaleString()} MMK
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: "#dc2626",
                              fontFamily: "Monaco, Menlo, monospace",
                            }}
                          >
                            {(treatmentPackage?.discountAmount || treatmentPackage?.totalDiscount || 0)?.toLocaleString()} MMK
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: "#475569",
                              fontWeight: 600,
                              fontFamily: "Monaco, Menlo, monospace",
                            }}
                          >
                            {(
                              treatmentPackage?.qty * treatmentPackage?.price
                            ).toLocaleString()}{" "}
                            MMK
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <div className="treatment-summary">
                <div className="treatment-summary-row">
                  <span className="treatment-summary-label">Subtotal</span>
                  <span className="treatment-summary-value">
                    {voucher?.totalAmount?.toLocaleString()} MMK
                  </span>
                </div>
                <div className="treatment-summary-row">
                  <span className="treatment-summary-label">Discount</span>
                  <span className="treatment-summary-value discount">
                    {voucher?.totalDiscount?.toLocaleString() || 0} MMK
                  </span>
                </div>
                <div className="treatment-summary-row">
                  <span className="treatment-summary-label total">
                    Total Due
                  </span>
                  <span className="treatment-summary-value total">
                    {(voucher?.totalAmount - (voucher?.totalDiscount || 0))?.toLocaleString()} MMK
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Footer Section */}
          <div className="voucher-footer">
            <div className="footer-info">
              Last updated{" "}
              {voucher?.updatedAt
                ? new Date(voucher.updatedAt).toLocaleDateString()
                : "N/A"}
            </div>
            {!voucher?.Refund && (
              <div className="footer-actions">
                <button className="btn btn-cancel">Cancel</button>
                <button
                  className="btn btn-approve-refund"
                  onClick={handleApprove}
                >
                  <RotateCcw className="btn-icon" />
                  Approve Refund
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={approveModal.open}
        onClose={closeApproveModal}
        aria-labelledby="approve-dialog-title"
        aria-describedby="approve-dialog-description"
      >
        <DialogTitle id="approve-dialog-title">
          Confirm Refund Approval
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="approve-dialog-description">
            Are you sure you want to approve this refund request?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeApproveModal}
            color="secondary"
            disabled={postLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmApprove}
            variant="contained"
            sx={{
              backgroundColor: "#4caf50",
              "&:hover": { backgroundColor: "#45a049" },
            }}
            autoFocus
            disabled={postLoading}
          >
            {postLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Approve"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default VoucherDetailPage;
